// ═══════════════════════════════════════════════════════════════════════════════
// Status Badge Component
// ═══════════════════════════════════════════════════════════════════════════════

import { useLanguage } from '../../contexts/LanguageContext';
import { APPROVAL_STATUS, RECEIPT_STATUS, type ApprovalStatus, type ReceiptStatus } from '@fdh/shared';

type StatusBadgeProps = {
  status: ApprovalStatus | ReceiptStatus;
  size?: 'xs' | 'sm' | 'md' | 'lg';
};

const STATUS_STYLES: Record<ApprovalStatus | ReceiptStatus, string> = {
  [APPROVAL_STATUS.PENDING]:
    'bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950/55 dark:text-yellow-100 dark:border-yellow-700',
  [APPROVAL_STATUS.APPROVED]:
    'bg-green-100 text-green-900 border-green-300 dark:bg-green-950/50 dark:text-green-100 dark:border-green-700',
  [APPROVAL_STATUS.REJECTED]:
    'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/45 dark:text-red-100 dark:border-red-800',
  [APPROVAL_STATUS.NEEDS_UPDATE]:
    'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-900/55 dark:text-orange-50 dark:border-orange-500',
  [APPROVAL_STATUS.CANCEL]:
    'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600',
  [RECEIPT_STATUS.SAVE]:
    'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/55 dark:text-amber-100 dark:border-amber-700',
  [RECEIPT_STATUS.SUBMITTED]:
    'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/55 dark:text-blue-100 dark:border-blue-700',
  [RECEIPT_STATUS.REVIEWED]:
    'bg-green-100 text-green-900 border-green-300 dark:bg-green-950/50 dark:text-green-100 dark:border-green-700',
};

const SIZE_STYLES = {
  xs: 'px-1.5 py-px text-[10px] leading-tight',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

const STATUS_I18N_KEYS: Record<ApprovalStatus | ReceiptStatus, string> = {
  [APPROVAL_STATUS.PENDING]: 'task.status.pending',
  [APPROVAL_STATUS.APPROVED]: 'task.status.approved',
  [APPROVAL_STATUS.REJECTED]: 'task.status.rejected',
  [APPROVAL_STATUS.NEEDS_UPDATE]: 'task.status.needsUpdate',
  [APPROVAL_STATUS.CANCEL]: 'task.status.cancel',
  [RECEIPT_STATUS.SAVE]: 'receipt.status.save',
  [RECEIPT_STATUS.SUBMITTED]: 'receipt.status.submitted',
  [RECEIPT_STATUS.REVIEWED]: 'receipt.status.reviewed',
};

export const StatusBadge = ({ status, size = 'md' }: StatusBadgeProps) => {
  const { t } = useLanguage();

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${STATUS_STYLES[status]} ${SIZE_STYLES[size]}`}
      role="status"
      aria-label={t(STATUS_I18N_KEYS[status])}
    >
      {t(STATUS_I18N_KEYS[status])}
    </span>
  );
};
